import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 */
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import cognizantLogoWhite from "../assets/cognizant-logo-white.svg";
import ByokDialog from "../byok/ByokDialog";
import { COGNIZANT_URL } from "../constants";
import { landingPalette } from "../theme";
// #region: Styled Components
const HeaderContainer = styled(Box) `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    background: ${landingPalette.header};
    border-bottom: 1px solid transparent;
    padding: 0.5rem 18px;
    z-index: 1200;
    display: flex;
    align-items: center;
`;
const Brand = styled(Box) `
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-width: 0;
`;
const BrandDivider = styled(Box) `
    width: 1px;
    height: 28px;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.45);
`;
const CognizantLogoLink = styled("a") `
    display: flex;
    align-items: center;
    padding-left: 0.15rem;
    flex-shrink: 0;
`;
const CognizantLogo = styled("img") `
    width: 160px;
    height: auto;
    display: block;
`;
const Nav = styled(Box) `
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 12px;
`;
const NavItems = styled(Box) `
    display: flex;
    align-items: center;
    gap: 12px;
`;
const NavLink = styled("button") `
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.92);
    font-size: 0.95rem;
    cursor: pointer;
    padding: 6px 8px;
    border-radius: 6px;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
    &.active {
        background: rgba(255, 255, 255, 0.16);
        font-weight: 600;
    }
`;
const NewSessionButton = styled("button") `
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.28);
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    &:hover {
        background: rgba(255, 255, 255, 0.14);
    }
`;
const Title = styled("h1") `
    color: #ffffff;
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    position: relative;
    bottom: 1px;
    display: flex;
    align-items: center;
    gap: 0.35em;
    white-space: nowrap;
`;
const TitleMuted = styled("span") `
    color: rgba(255, 255, 255, 0.92);
    font-weight: 400;
`;
const ToggleButton = styled("button") `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 6px;
    border-radius: 6px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #ffffff;
    margin-right: 12px;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;
// #endregion: Types
const Navbar = ({ hasSession = false, isLanding = false, active, onNavigate, onNewSession, ideasCount = 0, onDownloadIdeas, onToggleSidebar, }) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [byokOpen, setByokOpen] = useState(false);
    const openConfirm = () => setConfirmOpen(true);
    const closeConfirm = () => setConfirmOpen(false);
    const handleConfirm = () => {
        onNewSession?.();
        closeConfirm();
    };
    return (_jsxs(_Fragment, { children: [_jsxs(HeaderContainer, { children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center" }, children: [!isLanding && (_jsx(ToggleButton, { "data-testid": "navbar-toggle_sidebar-btn", onClick: () => onToggleSidebar?.(), "aria-label": "Toggle sidebar", children: _jsxs("svg", { width: "18", height: "14", viewBox: "0 0 18 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("rect", { y: "1", width: "18", height: "2", rx: "1", fill: "currentColor" }), _jsx("rect", { y: "6", width: "18", height: "2", rx: "1", fill: "currentColor" }), _jsx("rect", { y: "11", width: "18", height: "2", rx: "1", fill: "currentColor" })] }) })), _jsxs(Brand, { children: [_jsx(CognizantLogoLink, { href: COGNIZANT_URL, target: "_blank", rel: "noopener noreferrer", children: _jsx(CognizantLogo, { src: cognizantLogoWhite, alt: "Cognizant" }) }), _jsx(BrandDivider, { "aria-hidden": "true" }), _jsxs(Title, { children: ["LMX", _jsx(TitleMuted, { children: "AI and Human Brainstorming" })] })] })] }), _jsxs(Nav, { children: [_jsx(NavLink, { onClick: () => setByokOpen(true), children: "API Keys" }), hasSession && (_jsxs(NavItems, { children: [_jsx(NavLink, { className: active === "dashboard" ? "active" : "", onClick: () => onNavigate?.("dashboard"), children: "Dashboard" }), _jsx(NavLink, { className: active === "logs" ? "active" : "", onClick: () => onNavigate?.("logs"), children: "Logs" }), ideasCount > 0 && _jsx(NavLink, { onClick: () => onDownloadIdeas?.(), children: "Download CSV" })] })), !isLanding && (_jsx(NewSessionButton, { onClick: hasSession ? openConfirm : () => onNewSession?.(), children: "New session" }))] })] }), _jsx(ByokDialog, { isOpen: byokOpen, onClose: () => setByokOpen(false) }), _jsxs(Dialog, { open: confirmOpen, onClose: closeConfirm, "aria-labelledby": "new_session-confirm-title", "aria-describedby": "new_session-confirm-description", children: [_jsx(DialogTitle, { id: "new_session-confirm-title", children: "Create new session?" }), _jsx(DialogContent, { children: _jsx(DialogContentText, { id: "new_session-confirm-description", children: "All data will be lost upon creating a new session. Are you sure you want to continue?" }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: closeConfirm, color: "inherit", children: "Cancel" }), _jsx(Button, { onClick: handleConfirm, color: "primary", children: "Continue" })] })] })] }));
};
export default Navbar;
